<template>
  <section class="viewer-card-results">
    <div class="basic-loading-bounce-container" v-if="loading">
      <LoadingBounce></LoadingBounce>
    </div>
    <template v-else>
      <div class="viewer-card-results__container mx-auto">
        <div class="viewer-card-results__header-title">
          {{ assignedCard.title }}
        </div>
        <div class="viewer-card-results__header-subtitle">
          {{ gradeName }} {{ subjectName }}
        </div>

        <div class="row">
          <template v-if="chapterName">
            <div class="col-2 viewer-card-results__chapter-label">{{ $t('viewer.card.chapter.label') }}</div>
            <div class="col-10 viewer-card-results__chapter-value">{{ chapterName }}</div>
          </template>
          <template v-if="domainName">
            <div class="col-2 viewer-card-results__domain-label">{{ $t('viewer.card.domain.label') }}</div>
            <div class="col-10 viewer-card-results__domain-value">{{ domainName }}</div>
          </template>
        </div>

        <div class="row viewer-card-results__teacher">
          <div class="col-2"><img class="viewer-card-results__teacher-image" :src="teacherProfileImage" alt="teacher" /></div>
          <div class="col-10">
            <div class="viewer-card-results__teacher-title">{{ teacherName }}</div>
            <div class="viewer-card-results__teacher-class">
              {{ `${schoolName} ${classGrade !== null ? $t('common.grade_label', { grade: classGrade }) : ''} ${className}` }}
            </div>
          </div>
        </div>

        <div class="viewer-card-results__body-title">{{ $t('viewer.card.assignment.result.label') }}</div>

        <div class="row viewer-card-results__score">
          <div class="col-4">
            <div class="viewer-card-results__scroe-label">{{ $t('viewer.card.assignment.score.result') }}</div>
            <div class="viewer-card-results__scroe-value">
              <strong v-if="hasEssayQuizResult && isEssayQuizGraded" >{{ userScore || 0}}</strong>
              <strong v-else-if="hasEssayQuizResult && !isEssayQuizGraded" >-</strong>
              <strong v-else-if="!hasEssayQuizResult" >{{ userScore || 0}}</strong> / {{ $t('viewer.card.assignment.max.score') }}
            </div>
          </div>
          <div class="col-8">
            <div class="viewer-card-results__scroe-label">
              {{ $t('viewer.card.assignment.class.score') }}
            </div>
            <div class="viewer-card-results__scroe-value">
              <strong>{{ classAverageScore || 0}}</strong> / {{ $t('viewer.card.assignment.max.score') }}
            </div>
          </div>
        </div>

        <div class="row viewer-card-results__stats">
          <div class="col-4">
            <div class="viewer-card-results__stats-correct-answer-label">{{ $t('viewer.card.assignment.correct.answer') }}</div>
            <div class="viewer-card-results__stats-correct-answer-value">
              <span v-if="hasEssayQuizResult && isEssayQuizGraded" class="active">{{ correctQuizCount || 0}}</span>
              <span v-else-if="hasEssayQuizResult && !isEssayQuizGraded">-</span>
              <span v-else-if="!hasEssayQuizResult" class="active">{{ correctQuizCount || 0}}</span> / {{ assignedCard.quiz_count }}
            </div>
          </div>
          <div class="col-4">
            <div class="viewer-card-results__stats-rate-label">{{ $t('viewer.card.assignment.score.rate') }}</div>
            <div class="viewer-card-results__stats-rate-value">
              <span v-if="hasEssayQuizResult && isEssayQuizGraded">{{ correctRatio | numberToPercentage }}</span>
              <span v-else-if="hasEssayQuizResult && !isEssayQuizGraded">-%</span>
              <span v-else-if="!hasEssayQuizResult">{{ correctRatio | numberToPercentage }}</span>
            </div>
          </div>
          <div class="col-4" v-if="essayQuizCount > 0">
            <div class="viewer-card-results__stats-essay-label">{{ $t('viewer.card.assignment.essay.label') }}</div>
            <div class="viewer-card-results__stats-essay-count">
              <span :class="{ 'disabled' : !isEssayQuizGraded }">{{ essayQuizCount }}</span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-8 viewer-card-results__quiz-result-label">
            {{ $t('comp.card.results.quiz_result_chart') }}
          </div>
          <div class="col-4 viewer-card-results__quiz-result-action">
          </div>
        </div>

        <div class="viewer-card-results__quiz-result-chart">
          <QuizResultChart v-bind:pages="pageList" v-bind:card-id="cardId"></QuizResultChart>
        </div>

        <div
          v-if="isWrongPageList"
          class="card-results__wrong-answer row"
        >
          <div class="card-results__wrong-answer-label col-12">
            <span
              class="card-results__wrong-answer-button"
              @click="goToWrongPages()"
            >
              {{ $t('viewer.result.wrong.answers.action') }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="hasEssayQuizResult && isEssayQuizGraded"
        class="viewer-card-results__essay-results mx-auto"
      >
        <div class="viewer-card-results__essay-result-header">{{ $t('viewer.card.assignment.essay.description') }}</div>
        <div class="viewer-card-results__essay-result" v-for="quiz in essayQuizzes" :key="quiz.id">
          <div class="row">
            <div class="col-6 viewer-card-results__essay-result-quiz">{{ $t('viewer.page.header.label', { quizCount: quiz.position}) }}</div>
            <div class="col-6 viewer-card-results__essay-result-score">
              <strong>{{ quiz.submitted_answers[0].score || 0 }}</strong>/{{ $t('common.numeric_score', { n: quiz.score }) }}
            </div>
          </div>
          <div class="viewer-card-results__essay-result-description-header">{{ $t('viewer.card.assignment.essay.comment') }}</div>
          <div class="viewer-card-results__essay-result-description">
            {{ essayQuizDescription(quiz) }}
          </div>
          <div class="viewer-card-results__essay-result-action">
            <span class="clickable" @click="goToEssayQuizPage(quiz.position)">
              {{ $t('viewer.card.assignment.essay.result') }}
            </span>
          </div>
        </div>
      </div>

      <div class="viewer-card-results__btn-area mx-auto">
        <div class="row mx-auto">
          <div class="col-12 px-0">
            <button
              class="btn btn-inverted-primary btn-block"
              @click="goBackToClassting()"
            >
              {{ $t('viewer.card.assignment.learning.button') }}
            </button>
          </div>

          <div class="col-12 px-0">
            <button
              class="btn btn-primary btn-block"
              @click="goToQuizPages(1)"
            >
              {{ $t('viewer.card.assignment.review.button') }}
            </button>
          </div>
          <div
            v-if="!isDevice"
            class="col-12 px-0"
          >
            <button
              v-if="isAISupportCountry"
              class="btn btn-purple btn-block"
              @click="goToAIWeb()"
            >
              <template v-if="isValidSubjectGrade && isVaildUnit">
                {{ chapterName }} 단원 AI에서 학습하기
              </template>
              <template v-else-if="isValidSubjectGrade && !isVaildUnit">
                {{ subjectName }} 과목 AI에서 학습하기
              </template>
              <template v-else>
                클래스팅 AI에서 학습하기
              </template>
            </button>
          </div>
        </div>
      </div>

      <CustomModal
        v-if="isSubmissionError"
        id="assignment-submission-error--modal"
        class="basic-modal no-header-modal hide-cancel-btn content-report-modal"
        :hide-header="true"
        :ok-title="$t('common.confirm')"
        @ok="handleSubmissionErrorClick"
        :isHideCloseButton="true"
      >
        <template v-if="submissionError.status === 409">
          {{ $t('viewer.card.assignment.errros.already_submitted') }}
        </template>
        <template v-if="submissionError.status === 400">
          <div v-html="$t('viewer.card.assignment.errros.unknows')" />
        </template>
      </CustomModal>

    </template>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { isEmpty, sumBy, isNil, head, find } from 'lodash';
import QuizResultChart from '@/app/viewer-results/components/QuizResultChart';
import { CLASSTING_AI_WEB } from '@/config';
import Mobile from '@/mixins/Mobile';
import labels from '@/mixins/labels';

export default {
  name: 'card-results',

  mixins: [
    labels,
    Mobile,
  ],

  components: {
    QuizResultChart,
  },

  data() {
    return {
      loading: false,
      isSubmissionError: false,
      submissionError: {},
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentUserId: 'currentUserId',
      card: 'getCard',
      cardId: 'getCardId',
      pages: 'getPages',
      params: 'getRouteParams',
      result: 'getLearningResult',
      classRanks: 'getClassRanks',
      allRanks: 'getAllRanks',
      isEnd: 'getIsEnd',
      isDevice: 'getIsDevice',
      learningResult: 'getLearningResult',
      isClassStudent: 'isClassStudent',
      isAssignmentSubmitted: 'isAssignmentSubmitted',
      pageList: 'getPageList',
      wrongPages: 'getWrongPages',
      wrongPageList: 'getWrongPageList',
      essayQuizzes: 'getEssayQuizzes',
      quizResult: 'getQuizResult',
      quizzesCount: 'getQuizzesCount',
      gradeName: 'getGradeName',
      subjectName: 'getSubjectName',
      teacherProfileImage: 'getTeacherProfileImage',
      teacherName: 'getTeacherName',
      className: 'getClassName',
      schoolName: 'getSchoolName',
      chapterName: 'getChapterName',
      domainName: 'getDomainName',
      domainId: 'getDomainId',
      coreConcept: 'getCoreConcept',
      classGrade: 'getClassGrade',
      subjectGrades: 'subjectGrades',
      units: 'units',
      userGradeId: 'userGradeId',
      isAISupportCountry: 'isAISupportCountry',
    }),

    essayQuizCount() {
      if (isEmpty(this.essayQuizzes)) {
        return 0;
      }

      return this.essayQuizzes && this.essayQuizzes.length;
    },

    essayQuizResults() {
      if (isEmpty(this.essayQuizzes)) {
        return [];
      }

      return this.essayQuizzes.map(item => (
        item.submitted_answers[0]
      ));
    },

    hasEssayQuizResult() {
      return !isEmpty(this.essayQuizResults);
    },

    isEssayQuizGraded() {
      if (isEmpty(this.essayQuizResults)) {
        return false;
      }

      return this.essayQuizResults.every((item) => {
        if (!item) {
          return false;
        }

        return item.is_graded === true || item.is_graded === 1;
      });
    },

    isWrongPageList() {
      return !isEmpty(this.wrongPageList);
    },

    assignmentScore() {
      return this.card && this.card.score;
    },

    classAverageScore() {
      return this.assignmentScore && this.assignmentScore.class_score;
    },

    userScore() {
      return this.assignmentScore && this.assignmentScore.my_score;
    },

    classId() {
      return this.card && this.card.class_id;
    },

    correctQuizCount() {
      if (!isEmpty(this.result)) {
        return this.result.correct_quiz_count;
      }

      if (!isEmpty(this.pageList)) {
        return this.pageList.filter(page => (
          page.is_quiz && !isEmpty(page.submitted_answers) && page.submitted_answers[0].is_correct
        )).length;
      }

      return 0;
    },

    correctRatio() {
      if (!isEmpty(this.result)) {
        return parseInt((this.correctQuizCount / this.result.quiz_count) * 100, 10) || 0;
      }

      if (!isEmpty(this.assignedCard)) {
        return parseInt((this.correctQuizCount / this.assignedCard.quiz_count) * 100, 10) || 0;
      }

      return 0;
    },

    classRankRatio() {
      return (this.classRanks && this.classRanks.rank_ratio) || 0;
    },

    allRankRatio() {
      return (this.allRanks && this.allRanks.rank_ratio) || 0;
    },

    duration() {
      if (!isEmpty(this.result)) {
        return this.result.duration;
      }

      if (!isEmpty(this.pageList)) {
        return sumBy(this.pageList.filter(page => (
          page.is_quiz && !isEmpty(page.submitted_answers)
        )), page => (page.submitted_answers[0].duration));
      }

      return 0;
    },

    assignedCard() {
      return this.card.assigned_card;
    },

    notGradedCount() {
      if (isEmpty(this.pageList)) {
        return 0;
      }

      const notGraded = this.pageList.filter(page => (
        page.is_quiz &&
          ((!isEmpty(page.submitted_answers) && !page.submitted_answers[0].is_graded) ||
            (!isEmpty(page.submitted_answer) && !page.submitted_answer.is_graded))
      ));

      return notGraded.length;
    },

    isNotSubmittedAssignment() {
      return !isEmpty(this.learningResult)
          && !this.isAssignmentSubmitted
          && this.isClassStudent
          && !this.isEnd;
    },

    assignmentSubjectId() {
      return this.assignedCard.subject_id || null;
    },

    assignmentUnitId() {
      return this.assignedCard.unit_id || null;
    },

    isValidSubjectGrade() {
      const subjectGrade = find(this.subjectGrades, { subject_id: this.assignmentSubjectId });
      return !isEmpty(subjectGrade);
    },

    unit() {
      return find(this.units, { subject_id: this.assignmentUnitId });
    },

    isValidUnit() {
      return !isEmpty(this.unit);
    },

  },

  methods: {
    ...mapActions({
      updateAssignmentSubmission: 'updateAssignmentSubmission',
      fetchQuizResults: 'fetchQuizResults',
      fetchPageList: 'fetchPageList',
      fetchCard: 'fetchCard',
      fetchClassStudent: 'fetchClassStudent',
      setIsClassStudent: 'setIsClassStudent',
      setWrongPageList: 'setWrongPageList',
      resetWrongPageList: 'resetWrongPageList',
      fetchCoreConcept: 'fetchCoreConcept',
      fetchSubjectGrades: 'fetchSubjectGrades',
      fetchUnits: 'fetchUnits',
    }),

    essayQuizDescription(quiz) {
      const submittedAnswer = head(quiz.submitted_answers) || {};
      if (!isEmpty(submittedAnswer) && !isNil(submittedAnswer.comment)) {
        return submittedAnswer.comment;
      }

      return quiz.description || '문제 해설이 없습니다.';
    },

    resultParams() {
      const pages = Object.values(this.pages);
      let duration = 1;

      if (pages.length) {
        duration = (((new Date()).getTime() - pages[0].loaded_at.getTime()) / 1000) || 0;
      }

      const params = {
        card_id: this.params.cardId,
        duration,
      };
      return params;
    },

    goBackToClassting() {
      this.$ga.event({
        eventCategory: 'learning',
        eventAction: 'assignment_result.done',
        eventLabel: this.cardId,
      });

      if (this.isDevice) {
        return this.closeInAppBrowser();
      }

      window.close();
      return true;
    },

    closeInAppBrowser() {
      const APP_NAME = 'classting';

      this.call(`ctjscall://close?source=${APP_NAME}`);
    },

    goToWrongPages() {
      this.$ga.event({
        eventCategory: 'learning',
        eventAction: 'assignment_result.view_wrong_quiz',
        eventLabel: this.cardId,
      });

      const startPage = 1;
      this.setWrongPageList(this.wrongPageList)
        .then(() => (
          this.$router.push({
            name: 'pages.show',
            params: {
              cardId: this.cardId,
              page: startPage,
            },
          })
        ));
    },

    goToQuizPages() {
      this.$ga.event({
        eventCategory: 'learning',
        eventAction: 'assignment_result.view_all_quiz',
        eventLabel: this.cardId,
      });

      if (this.cardId) {
        this.$router.push({ name: 'pages.show', params: { cardId: this.cardId, page: 1 } });
      }
    },

    goToEssayQuizPage(position) {
      this.$ga.event({
        eventCategory: 'learning',
        eventAction: 'assignment_result.view_essay_quiz',
        eventLabel: this.cardId,
      });

      if (this.cardId && position) {
        this.$router.push({ name: 'pages.show', params: { cardId: this.cardId, page: position } });
      }
    },

    handleSubmissionErrorClick() {
      this.isSubmissionError = false;
      if (this.submissionError.status === 400) {
        window.location.href = `${window.location.origin}/viewer/cards/${this.cardId}/pages/1`;
        return true;
      }

      if (this.submissionError.status === 409) {
        window.location.href = `${window.location.origin}/viewer/cards/${this.cardId}`;
        return true;
      }

      return true;
    },

    goToAIWeb() {
      if (this.isValidSubjectGrade) {
        return window.open(`${CLASSTING_AI_WEB}/subjects/${this.assignmentSubjectId}/units/`);
      }

      return window.open(`${CLASSTING_AI_WEB}/home`);
    },
  },

  beforeMount() {
    this.loading = true;
    if (this.isClasstingAndroid) {
      this.callActionbar(true);
    }

    Promise.resolve()
      .then(() => {
        if (!isEmpty(this.wrongPages)) {
          this.resetWrongPageList();
        }

        if (isEmpty(this.card)) {
          return this.fetchCard(this.cardId)
            .then((card) => {
              if (isNil(card.assigned_card.core_concept_id)) {
                return true;
              }

              return this.fetchCoreConcept(card.assigned_card.core_concept_id);
            })
            .then(() => (
              this.fetchClassStudent({ classId: this.classId, studentId: this.currentUserId })
            ))
            .then(classStudent => (
              this.setIsClassStudent(!isNil(classStudent))
            ));
        }

        return true;
      })
      .then(() => {
        if (this.isNotSubmittedAssignment) {
          return this.updateAssignmentSubmission(this.resultParams())
            .catch((err) => {
              const { response } = err;

              if (
                response.status === 409
                  || response.status === 400
              ) {
                this.isSubmissionError = true;
                this.submissionError = response;
              }

              return true;
            });
        }

        return true;
      })
      .then(() => (
        this.fetchPageList(this.cardId)
      ))
      .then(() => {
        const params = {
          grade_id: this.userGradeId,
          is_activated: true,
        };

        return this.fetchSubjectGrades(params);
      })
      .then(() => {
        if (this.isValidSubjectGrade) {
          const params = {
            grade_id: this.userGradeId,
            subject_id: this.assignmentSubjectId,
          };

          return this.fetchUnits(params);
        }

        return true;
      })
      .then(() => {
        this.$ga.event({
          eventCategory: 'learning',
          eventAction: 'assignment_viewer.duration',
          eventLabel: this.cardId,
          eventValue: this.duration,
        });
        this.loading = false;
      })
      .catch(() => this.$root.$emit('bv::show::modal', 'basic-error-alert'));
  },
};
</script>

<style lang="scss" scoped>
  .viewer-card-results {
    background-color: #f5f5f5;
    padding: 13px 16px;

    @media (min-width: 576px) {
      padding-top: 28px;
      padding-bottom: 28px;
    }

    %viewer-card-results {
      background-color: #fff;
      padding-bottom: 24px;
      box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.26);
      border: solid 0.5px rgba(0, 0, 0, 0.07);
      border-radius: 4px;

      @media (min-width: 576px) {
        padding-top: 20px;
        padding-bottom: 34px;
        margin-bottom: 14px;
      }
    }

    .viewer-card-results__container {
      @extend %viewer-card-results;
      max-width: 570px;
      padding: 24px 20px;
      margin-bottom: 12px;
    }

    .viewer-card-results__header-label {
      font-size: 14px;
      line-height: 1.43;
      font-weight: bold;
      text-align: center;
      color: rgba(0, 0, 0, 0.87);
      margin-bottom: 8px;

      @media (min-width: 576px) {
        font-size: 16px;
        line-height: 1.5;
        margin-bottom: 4px;
      }
    }

    .viewer-card-results__header-title {
      font-size: 20px;
      line-height: 1.43;
      color: rgba(0, 0, 0, 0.87);

      @media (min-width: 576px) {
        font-size: 28px;
        line-height: 1.14;
      }
    }

    .viewer-card-results__header-subtitle {
      font-size: 14px;
      opacity: 0.54;
      color: #000000;
      padding-top: 8px;
      padding-bottom: 18px;
    }

    .viewer-card-results__summary {
      @media (min-width: 576px) {
      }

      .viewer-card-results__summary-label,
      .viewer-card-results__summary-value {
        margin-top: 18px;
      }

      .viewer-card-results__summary-label {
        padding-top: 4px;
        padding-bottom: 4px;
      }
    }

    .viewer-card-results__summary-mobile {
      max-width: 360px;
      padding-left: 13px;
      padding-right: 13px;
      margin-bottom: 29px;

      .col-4:not(:last-child),
      .col-6:not(:last-child) {
        border-right: 1px solid rgba(0, 0, 0, 0.1);
      }

      .viewer-card-results__summary-label {
        margin-bottom: 4px;
      }
    }

    .viewer-card-results__summary-label {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.43;
      color: rgba(0, 0, 0, 0.54);
    }

    .viewer-card-results__summary-value {
      font-size: 20px;
      line-height: 1.4;
      color: rgba(0, 0, 0, 0.87);

      @media (min-width: 480px) {
        font-size: 20px;
      }
    }

    .viewer-card-results__chapter-label {
      color: #000000;
      opacity: 0.54;
      font-size: 14px;
    }

    .viewer-card-results__chapter-value {
      color: rgba(0, 0, 0, 0.87);
      font-size: 14px;
    }

    .viewer-card-results__domain-label {
      color: #000000;
      opacity: 0.54;
      font-size: 14px;
    }

    .viewer-card-results__domain-value {
      color: rgba(0, 0, 0, 0.87);
      font-size: 14px;
    }

    .viewer-card-results__teacher {
      padding-top: 24px;
      padding-bottom: 24px;

      .viewer-card-results__teacher-title {
        font-size: 14px;
      }

      .viewer-card-results__teacher-class {
        font-size: 14px;
      }

      .viewer-card-results__teacher-image {
        width: 38px;
        height: 38px;
        border: solid 1px #c3c3c3;
        border-radius: 38px;

        @media (min-width: 480px) {
          width: 44px;
          height: 44px;
        }
      }

      .viewer-card-results__techart-name {
        font-size: 12px;
        font-weight: bold;
      }

      .viewer-card-results__teacher-class {
        opacity: 0.54;
        color: #000000;
      }

    }
    .viewer-card-results__body-title {
      font-size: 14px;
      font-weight: bold;
    }

    .viewer-card-results__score {
      padding-top: 20px;
      padding-bottom: 10px;

      .viewer-card-results__scroe-label {
        font-size: 14px;
        color: rgba(0, 0, 0, 0.54);
      }

      .viewer-card-results__scroe-value {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.54);

        strong {
          color: rgba(0, 0, 0, 0.87);
          font-size: 16px;
        }
      }
    }

    .viewer-card-results__stats {
      padding-top: 10px;
      padding-bottom: 46px;

      .viewer-card-results__stats-correct-answer-label,
      .viewer-card-results__stats-rate-label,
      .viewer-card-results__stats-essay-label {
        font-size: 14px;
        color: rgba(0, 0, 0, 0.54);
      }

      .viewer-card-results__stats-correct-answer-value,
      .viewer-card-results__stats-rate-value {
        font-size: 16px;
        color: rgba(0, 0, 0, 0.87);

        .active {
          color: #00c896;
        }
      }

      .viewer-card-results__stats-essay-count {
        font-size: 16px;
        color: rgba(0, 0, 0, 0.87);

        .disabled {
          color: rgba(0, 0, 0, 0.54);
        }
      }
    }

    .viewer-card-results__quiz-result-label {
      font-size: 14px;
      font-weight: bold;
      line-height: 1.14;
      color: #000000;
      margin-bottom: 10px;

      @media (min-width: 576px) {
        margin-bottom: 14px;
      }
    }

    .viewer-card-results__quiz-result-action {
      text-align: right;
      margin-bottom: 14px;
      font-size: 12px;

      span {
        opacity: 0.54;
        color: #000000;
        cursor: pointer;
      }
    }

    .viewer-card-results__quiz-result-chart {
      line-height: 10px;
      max-width: 360px;
    }

    .viewer-card-results__btn-area {
      max-width: 570px;

      @media (min-width: 576px) {
        padding-top: 28px;
      }

      @media (max-width: 575px) {
        .row {
          margin-left: -6px;
          margin-right: -6px;

          .col-6 {
            padding-left: 6px;
            padding-right: 6px;
          }
        }
      }

      .btn-block {
        font-weight: 400;
        border-radius: 4px;
        padding-top: 16px;
        padding-bottom: 16px;
        margin-bottom: 12px;
      }

      .card-action-btn {
        background: #00c896;
        color: #ffffff;
      }
    }
  }

  .viewer-card-results__essay-results {
    max-width: 570px;
    margin-top: 48px;

    .viewer-card-results__essay-result-header {
      font-size: 14px;
      color: rgba(0, 0, 0, 0.54);
      padding-bottom: 10px;
    }

    .viewer-card-results__essay-result {
      @extend %viewer-card-results;
      padding: 24px 22px;
      margin-bottom: 12px;

      .viewer-card-results__essay-result-quiz {
        color: rgba(0, 0, 0, 0.54);
        font-size: 12px;
        text-align: left;
      }

      .viewer-card-results__essay-result-score {
        color: rgba(0, 0, 0, 0.87);
        font-size: 16px;
        text-align: right;
      }

      .viewer-card-results__essay-result-description-header {
        font-size: 12px;
        font-weight: bold;
        padding-top: 24px;
        padding-bottom: 14px;
        color: #ffc107;
      }

      .viewer-card-results__essay-result-description {
        color: rgba(0, 0, 0, 0.87);
        font-size: 14px;
        word-wrap: break-word;
      }

      .viewer-card-results__essay-result-action {
        text-align: right;
        font-weight: bold;
        font-size: 12px;
        color: rgba(0, 0, 0, 0.54);
        padding: 14px 0 0 0;
      }
    }
  }


  .card-results__wrong-answer {
    padding-top: 24px;

    .card-results__wrong-answer-label {
      font-size: 12px;
      text-align: center;
      opacity: 0.87;
      color: rgba(0, 0, 0, 0.87);
    }

    .card-results__wrong-answer-button {
      color: #ff595f;
      border: 0;
      font-size: 14px;
      height: 22px;
      cursor: pointer;
      text-align: left;
    }
  }

</style>
